import './applySearchMakePage.scss';

import KeyboardBackspaceRoundedIcon from '@mui/icons-material/KeyboardBackspaceRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import { get, isEmpty } from 'lodash';
import { useEffect } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { NavigationPane } from '../../../components/shared_new/NavigationPane/NavigationPane';
import { RadioGroup } from '../../../components/shared_new/RadioGroup/RadioGroup';
import { RadioOptionClickEvent } from '../../../components/shared_new/RadioGroup/types/RadioOptionClickEvent';
import { RadioGroupLoading } from '../../../components/shared_new/RadioGroupLoading/RadioGroupLoading';
import { useDelayedLoading } from '../../../store/apiStore/hooks/useDelayedLoading';
import useApplyStore from '../../../store/applyStore/useApplyStore';
import useNavigationPaneStore from '../../../store/navigationPaneStore/useNavigationPaneStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';
import { isValidClickEvent } from '../../../utils/component/isValidClickEvent';

export function Component(): JSX.Element {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { next, previous } = useWorkflowStore();
  const { setDrawerOpen } = useNavigationPaneStore();

  const {
    make,
    makes,
    fetchVehicleMakes,
    fetchVehicleMakesFailedMessage,
    isFetchingVehicleMakes,
    setMakeByValue,
  } = useApplyStore();

  const { register, handleSubmit, watch } = useForm({
    defaultValues: {
      makeValue: get(make, 'value'),
    },
  });

  const isLoading = useDelayedLoading(isFetchingVehicleMakes);

  useEffect(() => {
    fetchVehicleMakes();
  }, []);

  const formSubmit = (values: FieldValues): void => {
    const { makeValue } = values;

    if (makeValue) {
      setMakeByValue(makeValue);

      navigate(next());
    }
  };

  const previousStep = (): void => {
    navigate(previous());
  };

  const radioOptionClicked = (event: RadioOptionClickEvent<string>): void => {
    if (
      !('target' in event && 'value' in event.target) ||
      !isValidClickEvent(event)
    ) {
      return;
    }

    formSubmit({ makeValue: event.target.value });
  };

  return (
    <div className="apply-search-make-page">
      <ThemeProvider theme={theme}>
        <Box sx={theme.pageLayout}>
          <header data-testid={locator.pages.applySearchMake.header.root}>
            <button
              aria-label="Previous step"
              data-testid={
                locator.pages.applySearchMake.header.previousStepButton
              }
              type="button"
              onClick={previousStep}
            >
              <KeyboardBackspaceRoundedIcon />
            </button>
            <button
              aria-label="Toggle drawer"
              data-testid={locator.pages.applySearchMake.header.drawerButton}
              type="button"
              onClick={() => {
                setDrawerOpen(true);
              }}
            >
              <MenuRoundedIcon />
            </button>
          </header>

          <div className="progress-container" role="complementary">
            <LinearProgress
              aria-label="Application completed percentage"
              data-testid={locator.pages.applySearchMake.progress.root}
              value={useWorkflowStore.getState().computed.progress}
              variant="determinate"
            />
          </div>

          <main>
            <form
              data-testid={locator.pages.applySearchMake.form.root}
              noValidate
              onSubmit={handleSubmit(formSubmit)}
            >
              <div className="form-content">
                <h1 data-testid={locator.pages.applySearchMake.form.h1}>
                  {t('ApplySearchMakePage.title')}
                </h1>
                <h2 data-testid={locator.pages.applySearchMake.form.h2}>
                  {t('ApplySearchMakePage.subtitle')}
                </h2>

                {!isLoading && !isEmpty(fetchVehicleMakesFailedMessage) && (
                  <div style={{ marginBottom: '2rem' }}>
                    <Alert
                      data-testid={locator.pages.applySearchMake.alert.error}
                      icon={false}
                      severity="error"
                    >
                      {fetchVehicleMakesFailedMessage}
                    </Alert>
                  </div>
                )}

                <div style={{ marginBottom: '3rem' }}>
                  {isLoading && (
                    <RadioGroupLoading
                      loadingCount={10}
                      variant="stacked-compact"
                    />
                  )}

                  {!isLoading && (
                    <div>
                      <RadioGroup
                        defaultValue={get(make, 'value')}
                        name="makeValue"
                        options={makes.map((makeObj, index) => ({
                          label: makeObj.label,
                          value: makeObj.value,
                          testId:
                            locator.pages.applySearchMake.form.getRadioOption(
                              index,
                            ),
                        }))}
                        register={register}
                        testId={
                          locator.pages.applySearchMake.form.radioGroupRoot
                        }
                        variant="stacked-compact"
                        watch={watch}
                        onClick={radioOptionClicked}
                      />
                    </div>
                  )}
                </div>

                {/* allows form submission using the keyboard without a visible button*/}
                <button
                  aria-hidden="true"
                  data-testid={
                    locator.pages.applySearchMake.form.accessibilitySubmit
                  }
                  style={{ display: 'none' }}
                  type="submit"
                />
              </div>
              {!isLoading && (
                <footer data-testid={locator.pages.applySearchMake.footer.root}>
                  {t('copyright', { year: new Date().getFullYear() })}
                </footer>
              )}
            </form>
          </main>
        </Box>

        <NavigationPane withDrawer withUserNameHeader />
      </ThemeProvider>
    </div>
  );
}

Component.displayName = 'ApplySearchMakePage';
